import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import {
  ArrowUp,
  ArrowDown,
  Trash2,
  Plus,
  Save,
  Sparkles,
  AlertTriangle,
  GripVertical,
} from "lucide-react";

const ICON_OPTIONS = [
  "user",
  "flag",
  "target",
  "message-circle",
  "bot",
  "film",
  "test-tube",
  "flask-conical",
  "trending-up",
  "rocket",
];

const LIVE_ENGINE_STAGES = [
  "LIVE_PRETEST",
  "LIVE_TEST",
  "LIVE_PRESCALE",
  "LIVE_SCALE",
];

function newItem(label = "Nova tarefa") {
  return {
    id: null,
    label,
    description: null,
    required: true,
  };
}

function newStep(mode = "caverna") {
  return {
    id: null,
    mode,
    key_slug: null,
    name: mode === "caverna" ? "Novo passo Caverna" : "Novo passo Live",
    description: "Descreva o que o aluno precisa fazer.",
    icon: mode === "caverna" ? "flag" : "rocket",
    is_required: true,
    engine_stage: mode === "live" ? "LIVE_PRETEST" : null,
    checklist: [newItem("Primeira tarefa")],
    instruction_video_enabled: false,
    instruction_video_title: "",
    instruction_video_description: "",
    instruction_video_url: "",
    instruction_video_storage_path: "",
  };
}

export default function JourneyEditor({ mentorships = [], selectedMentorship = "", onSelectMentorship } = {}) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const fetchEditor = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedMentorship) params.mentorship_id = selectedMentorship;
      const res = await api.get("/api/mentorship/journey/editor", { params });
      setSteps(res.data.steps || []);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Não foi possível carregar a jornada."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedMentorship]);

  useEffect(() => {
    fetchEditor();
  }, [fetchEditor]);

  const move = (index, dir) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updateStep = (index, patch) => {
    setSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const removeStep = (index) => {
    if (!window.confirm("Remover este passo? O progresso dos alunos neste passo será apagado.")) return;
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const addStep = (mode) => {
    setSteps((prev) => [...prev, newStep(mode)]);
  };

  const updateItem = (stepIdx, itemIdx, patch) => {
    setSteps((prev) => {
      const next = [...prev];
      const items = [...(next[stepIdx].checklist || [])];
      items[itemIdx] = { ...items[itemIdx], ...patch };
      next[stepIdx] = { ...next[stepIdx], checklist: items };
      return next;
    });
  };

  const addItem = (stepIdx) => {
    setSteps((prev) => {
      const next = [...prev];
      const items = [...(next[stepIdx].checklist || []), newItem()];
      next[stepIdx] = { ...next[stepIdx], checklist: items };
      return next;
    });
  };

  const removeItem = (stepIdx, itemIdx) => {
    setSteps((prev) => {
      const next = [...prev];
      const items = (next[stepIdx].checklist || []).filter((_, i) => i !== itemIdx);
      next[stepIdx] = { ...next[stepIdx], checklist: items };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        steps: steps.map((s) => ({
          id: s.id || null,
          mode: s.mode,
          key_slug: s.key_slug || null,
          name: s.name,
          description: s.description,
          icon: s.icon || "flag",
          is_required: s.is_required !== false,
          engine_stage: s.mode === "live" ? s.engine_stage : null,
          checklist: (s.checklist || []).map((it) => ({
            id: it.id || null,
            label: it.label,
            description: it.description || null,
            required: it.required !== false,
          })),
          instruction_video_enabled: !!s.instruction_video_enabled,
          instruction_video_source_type: s.instruction_video_source_type || null,
          instruction_video_title: s.instruction_video_title || null,
          instruction_video_description: s.instruction_video_description || null,
          instruction_video_url: s.instruction_video_url || null,
          instruction_video_storage_path: s.instruction_video_storage_path || null,
        })),
      };
      const config = { params: {} };
      if (selectedMentorship) config.params.mentorship_id = selectedMentorship;
      await api.put("/api/mentorship/journey", payload, config);
      setMessage("Jornada salva com sucesso.");
      await fetchEditor();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar jornada.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        Carregando editor da jornada...
      </div>
    );
  }

  return (
    <div data-testid="journey-editor" className="space-y-4">
      {/* Mentorship selector for platform owner / multi-mentorship users */}
      {mentorships.length > 1 && (
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-bold text-gray-400">Mentoria:</label>
          <select
            data-testid="journey-editor-mentorship-selector"
            value={selectedMentorship}
            onChange={(e) => onSelectMentorship && onSelectMentorship(e.target.value)}
            className="bg-[#14161C] border border-[#282D3D] rounded-lg py-1.5 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
          >
            <option value="">Minha mentoria principal</option>
            {mentorships.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Header + save */}
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-[#FF6B00] uppercase tracking-wider mb-1">
            <Sparkles className="w-4 h-4" />
            <span>Editor da Jornada</span>
          </div>
          <h2 className="text-lg font-extrabold text-white">
            Configure a trilha do aluno
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Reordene com as setas, edite textos, ícones e as tarefas de cada passo.
            Modo Live é governado pelo motor operacional — a ordem dos passos Live segue as fases (Pré-Teste → Escala).
          </p>
        </div>
        <button
          data-testid="journey-editor-save"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? "Salvando..." : "Salvar Jornada"}
        </button>
      </div>

      {error && (
        <div
          data-testid="journey-editor-error"
          className="rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs p-3 flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {message && (
        <div
          data-testid="journey-editor-success"
          className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs p-3"
        >
          {message}
        </div>
      )}

      {/* Steps list */}
      <div className="space-y-3">
        {steps.map((step, index) => (
          <div
            key={step.id || `new-${index}`}
            data-testid={`editor-step-${index}`}
            className={`rounded-xl border p-3 sm:p-4 bg-[#14161C] ${
              step.mode === "live"
                ? "border-purple-500/30"
                : "border-[#282D3D]"
            }`}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-widest">
                <GripVertical className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">#{index + 1}</span>
                <span
                  className={
                    step.mode === "live" ? "text-purple-300" : "text-[#FF6B00]"
                  }
                >
                  {step.mode === "live" ? "Modo Live" : "Modo Caverna"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid={`editor-move-up-${index}`}
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  className="p-1.5 rounded-md text-gray-300 hover:bg-[#1F2330] disabled:opacity-30"
                  title="Mover para cima"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  data-testid={`editor-move-down-${index}`}
                  onClick={() => move(index, 1)}
                  disabled={index === steps.length - 1}
                  className="p-1.5 rounded-md text-gray-300 hover:bg-[#1F2330] disabled:opacity-30"
                  title="Mover para baixo"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button
                  data-testid={`editor-delete-step-${index}`}
                  onClick={() => removeStep(index)}
                  className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10"
                  title="Remover passo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Basic fields */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
              <label className="col-span-2 block">
                <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Nome</span>
                <input
                  data-testid={`editor-step-name-${index}`}
                  value={step.name}
                  onChange={(e) => updateStep(index, { name: e.target.value })}
                  className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Ícone</span>
                <select
                  data-testid={`editor-step-icon-${index}`}
                  value={step.icon}
                  onChange={(e) => updateStep(index, { icon: e.target.value })}
                  className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                >
                  {ICON_OPTIONS.map((ico) => (
                    <option key={ico} value={ico}>
                      {ico}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Modo</span>
                <select
                  data-testid={`editor-step-mode-${index}`}
                  value={step.mode}
                  onChange={(e) => {
                    const mode = e.target.value;
                    updateStep(index, {
                      mode,
                      engine_stage: mode === "live" ? step.engine_stage || "LIVE_PRETEST" : null,
                    });
                  }}
                  className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                >
                  <option value="caverna">Caverna</option>
                  <option value="live">Live</option>
                </select>
              </label>
            </div>

            {step.mode === "live" && (
              <label className="block mb-2">
                <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">
                  Fase do motor operacional
                </span>
                <select
                  data-testid={`editor-step-engine-${index}`}
                  value={step.engine_stage || ""}
                  onChange={(e) => updateStep(index, { engine_stage: e.target.value })}
                  className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                >
                  {LIVE_ENGINE_STAGES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="block mb-2">
              <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Descrição</span>
              <textarea
                data-testid={`editor-step-desc-${index}`}
                rows={2}
                value={step.description}
                onChange={(e) => updateStep(index, { description: e.target.value })}
                className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs focus:outline-none focus:border-[#FF6B00] resize-y"
              />
            </label>

            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase font-bold text-gray-400">
                  Tarefas ({(step.checklist || []).length})
                </span>
                <button
                  data-testid={`editor-add-item-${index}`}
                  onClick={() => addItem(index)}
                  className="text-[11px] font-bold text-[#FF6B00] flex items-center gap-1 hover:text-[#FF8533]"
                >
                  <Plus className="w-3.5 h-3.5" /> Nova tarefa
                </button>
              </div>
              <div className="space-y-1.5">
                {(step.checklist || []).map((it, itIdx) => (
                  <div
                    key={it.id || `nit-${itIdx}`}
                    className="flex items-center gap-2 bg-[#0F1219] border border-[#282D3D] rounded-lg p-2"
                  >
                    <input
                      data-testid={`editor-item-label-${index}-${itIdx}`}
                      value={it.label}
                      onChange={(e) => updateItem(index, itIdx, { label: e.target.value })}
                      className="flex-1 bg-transparent text-white text-xs focus:outline-none"
                    />
                    <label className="text-[10px] text-gray-400 flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={it.required !== false}
                        onChange={(e) => updateItem(index, itIdx, { required: e.target.checked })}
                        className="accent-[#FF6B00]"
                      />
                      Obrig.
                    </label>
                    <button
                      data-testid={`editor-item-remove-${index}-${itIdx}`}
                      onClick={() => removeItem(index, itIdx)}
                      className="p-1 rounded text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {(step.checklist || []).length === 0 && (
                  <div className="text-[11px] text-gray-500 italic py-1">
                    Nenhuma tarefa. Adicione ao menos uma para o aluno concluir o passo.
                  </div>
                )}
              </div>
            </div>

            {/* Instructional video */}
            <div className="mt-3 pt-3 border-t border-[#282D3D]">
              <label className="flex items-center gap-2 text-[10px] uppercase font-bold text-purple-300 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  data-testid={`editor-video-enable-${index}`}
                  checked={!!step.instruction_video_enabled}
                  onChange={(e) => updateStep(index, { instruction_video_enabled: e.target.checked })}
                  className="accent-purple-500"
                />
                🎥 Vídeo explicativo
              </label>
              {step.instruction_video_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select
                    data-testid={`editor-video-source-${index}`}
                    value={step.instruction_video_source_type || "external_url"}
                    onChange={(e) => updateStep(index, { instruction_video_source_type: e.target.value })}
                    className="bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs focus:outline-none focus:border-purple-500"
                  >
                    <option value="external_url">URL externa (YouTube / Vimeo / mp4)</option>
                    <option value="google_drive">Link do Google Drive</option>
                    <option value="dropbox">Link do Dropbox</option>
                    <option value="upload">Upload (Supabase Storage)</option>
                  </select>
                  <input
                    data-testid={`editor-video-title-${index}`}
                    placeholder="Título do vídeo"
                    value={step.instruction_video_title || ""}
                    onChange={(e) => updateStep(index, { instruction_video_title: e.target.value })}
                    className="bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs focus:outline-none focus:border-purple-500"
                  />
                  <input
                    data-testid={`editor-video-url-${index}`}
                    placeholder="URL do vídeo ou link compartilhável"
                    value={step.instruction_video_url || ""}
                    onChange={(e) => updateStep(index, { instruction_video_url: e.target.value })}
                    className="sm:col-span-2 bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs focus:outline-none focus:border-purple-500"
                  />
                  <textarea
                    data-testid={`editor-video-desc-${index}`}
                    rows={2}
                    placeholder="Descrição curta"
                    value={step.instruction_video_description || ""}
                    onChange={(e) => updateStep(index, { instruction_video_description: e.target.value })}
                    className="sm:col-span-2 bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs focus:outline-none focus:border-purple-500 resize-y"
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add step buttons */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          data-testid="editor-add-caverna-step"
          onClick={() => addStep("caverna")}
          className="flex-1 py-2 rounded-lg bg-[#1F2330] hover:bg-[#282F42] border border-[#282D3D] text-white text-xs font-bold flex items-center justify-center gap-1"
        >
          <Plus className="w-3.5 h-3.5 text-[#FF6B00]" /> Novo passo Caverna
        </button>
        <button
          data-testid="editor-add-live-step"
          onClick={() => addStep("live")}
          className="flex-1 py-2 rounded-lg bg-[#1F2330] hover:bg-[#282F42] border border-purple-500/40 text-white text-xs font-bold flex items-center justify-center gap-1"
        >
          <Plus className="w-3.5 h-3.5 text-purple-300" /> Novo passo Live
        </button>
      </div>
    </div>
  );
}
